/**
 * User Profile Page
 * Main profile page with tabbed interface for users to view and manage their profile
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Group,
  Card,
  Badge,
  Tabs,
  Text,
  Avatar,
  Center,
  Loader,
} from '../../components/ui';
import {
  IconUser,
  IconLock,
  IconShield,
  IconKey,
  IconDevices,
  IconHistory,
  IconChartBar,
  IconUserCircle,
} from '@tabler/icons-react';
import { useEnrichedAuth } from '../../hooks/useEnrichedAuth';
import { useUserService } from '@actschurch/ccms-sdk';
import { FadeIn, SlideIn } from '../../components/animations';
import type { UserModelView, DeviceModel, LoginAttemptModel } from './types';

// Import tab components (will be created)
import { AccountDetailsTab } from './components/AccountDetailsTab';
import { MemberProfile } from '../../components/members';
import { RolesTab } from './components/RolesTab';
import { RightsTab } from './components/RightsTab';
import { PasswordChangeTab } from './components/PasswordChangeTab';
import PresenceAnalyticsPage from './PresenceAnalyticsPage';

// Import admin components for reuse
import { DevicesTab } from '../admin/Users/components/DevicesTab';
import { LoginActivityTab } from '../admin/Users/components/LoginActivityTab';

const PROFILE_TABS = ['account', 'member', 'roles', 'rights', 'analytics', 'password', 'devices', 'activity'];

export default function ProfilePage() {
  const { user: authUser, isAuthenticated, isLoading: authLoading } = useEnrichedAuth();
  const userService = useUserService();

  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab=member deep-links to the member profile (used by the "Complete your profile" banner).
  const [activeTab, setActiveTab] = useState<string>(() => {
    const requested = (searchParams.get('tab') ?? '').toLowerCase();
    return PROFILE_TABS.includes(requested) ? requested : 'account';
  });
  const [userData, setUserData] = useState<UserModelView | null>(null);
  const [rolesCount, setRolesCount] = useState<number>(0);
  const [permissionsCount, setPermissionsCount] = useState<number>(0);
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttemptModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);

  // Fetch initial user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!authUser?.id) return;

      setLoading(true);
      try {
        const currentUser = await userService.getCurrentUser();
        setUserData(currentUser);

        // Fetch roles and permissions count
        const [roles, permissions] = await Promise.all([
          userService.getUserRoles(authUser.id),
          userService.getUserPermissions(authUser.id),
        ]);

        setRolesCount(roles?.length || 0);
        setPermissionsCount(permissions?.length || 0);
      } catch (error) {
        console.error('[ProfilePage] Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && authUser) {
      fetchUserData();
    }
  }, [authUser, isAuthenticated, userService]);

  // Fetch devices when devices tab is activated - using "me" endpoint
  const fetchDevices = async () => {
    if (!authUser?.id) return;

    setDevicesLoading(true);
    try {
      // Use "me/devices" endpoint for current user's devices
      const devices = await userService.getMyDevices();
      setDevices(devices || []);
    } catch (error) {
      console.error('[ProfilePage] Error fetching devices:', error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  // Fetch login activity when login activity tab is activated - using "me" endpoint
  const fetchLoginActivity = async () => {
    if (!authUser?.id) return;

    setLoginActivityLoading(true);
    try {
      // Use "me/login-history" endpoint for current user's login history
      const loginAttempts = await userService.getMyLoginHistory({ page: 1, pageSize: 50 });
      setLoginAttempts(loginAttempts || []);
    } catch (error) {
      console.error('[ProfilePage] Error fetching login activity:', error);
      setLoginAttempts([]);
    } finally {
      setLoginActivityLoading(false);
    }
  };

  // A deep link (?tab=devices / ?tab=activity) lands on a lazy tab without a click: load it once.
  useEffect(() => {
    if (!authUser?.id) return;
    if (activeTab === 'devices') fetchDevices();
    else if (activeTab === 'activity') fetchLoginActivity();
    // Only on arrival; later switches go through handleTabChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  // Handle tab change with lazy loading
  const handleTabChange = (value: string | null) => {
    if (!value) return;
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'account') next.delete('tab'); else next.set('tab', value);
    if (value !== 'member') next.delete('section');
    setSearchParams(next, { replace: true });

    // Lazy load data for specific tabs
    if (value === 'devices' && devices.length === 0) {
      fetchDevices();
    } else if (value === 'activity' && loginAttempts.length === 0) {
      fetchLoginActivity();
    }
  };

  // Handle device delete
  const handleDeviceDelete = async () => {
    await fetchDevices();
  };

  // Handle device toggle
  const handleDeviceToggle = async () => {
    await fetchDevices();
  };

  // Handle user data update
  const handleUserUpdate = async () => {
    if (!authUser?.id) return;

    try {
      const currentUser = await userService.getCurrentUser();
      setUserData(currentUser);
    } catch (error) {
      console.error('[ProfilePage] Error refreshing user data:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <Container size="xl" py="xl">
        <Center style={{ minHeight: '400px' }}>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (!isAuthenticated || !authUser) {
    return (
      <Container size="xl" py="xl">
        <Center style={{ minHeight: '400px' }}>
          <Text>Please log in to view your profile.</Text>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header Section */}
        <FadeIn>
          <Card withBorder p="xl">
            <Group align="flex-start" wrap="nowrap">
              {/* Avatar */}
              <Avatar
                src={userData?.avatar || authUser.avatar}
                alt={userData?.displayName || authUser.displayName}
                size={120}
                radius="md"
                userId={authUser.id}
                showPresence
                showPresenceTooltip
                gradientBorder
                gradientVariant="primary"
              />

              {/* User Info */}
              <Stack gap="xs" style={{ flex: 1 }}>
                <div>
                  <Text size="xl" fw={700}>
                    {userData?.displayName || authUser.displayName || `${authUser.firstName} ${authUser.surname}`}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {userData?.email || authUser.email}
                  </Text>
                </div>

                {/* Quick Stats */}
                <Group gap="md" mt="sm">
                  <Badge size="lg" variant="light" color="blue" leftSection={<IconShield size={14} />}>
                    {rolesCount} {rolesCount === 1 ? 'Role' : 'Roles'}
                  </Badge>
                  <Badge size="lg" variant="light" color="grape" leftSection={<IconKey size={14} />}>
                    {permissionsCount} {permissionsCount === 1 ? 'Permission' : 'Permissions'}
                  </Badge>
                  {userData?.emailConfirmed && (
                    <Badge size="lg" variant="light" color="green">
                      Email Verified
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Group>
          </Card>
        </FadeIn>

        {/* Tabs Section */}
        <SlideIn delay={100}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tabs.List>
              <Tabs.Tab value="account" leftSection={<IconUser size={16} />}>
                Account
              </Tabs.Tab>
              <Tabs.Tab value="member" leftSection={<IconUserCircle size={16} />}>
                Member profile
              </Tabs.Tab>
              <Tabs.Tab value="roles" leftSection={<IconShield size={16} />}>
                Roles
              </Tabs.Tab>
              <Tabs.Tab value="rights" leftSection={<IconKey size={16} />}>
                Rights
              </Tabs.Tab>
              <Tabs.Tab value="analytics" leftSection={<IconChartBar size={16} />}>
                Analytics
              </Tabs.Tab>
              <Tabs.Tab value="password" leftSection={<IconLock size={16} />}>
                Password
              </Tabs.Tab>
              <Tabs.Tab value="devices" leftSection={<IconDevices size={16} />}>
                Devices
              </Tabs.Tab>
              <Tabs.Tab value="activity" leftSection={<IconHistory size={16} />}>
                Login Activity
              </Tabs.Tab>
            </Tabs.List>

            {/* Tab Panels */}
            <Tabs.Panel value="account" pt="md">
              <AccountDetailsTab userId={authUser.id} user={userData} onUpdate={handleUserUpdate} />
            </Tabs.Panel>

            <Tabs.Panel value="member" pt="md">
              {/* The shared member profile in self mode (api/members/me/*). */}
              {activeTab === 'member' && <MemberProfile mode="self" tabParam="section" />}
            </Tabs.Panel>

            <Tabs.Panel value="roles" pt="md">
              <RolesTab userId={authUser.id} user={userData} />
            </Tabs.Panel>

            <Tabs.Panel value="rights" pt="md">
              <RightsTab userId={authUser.id} user={userData} />
            </Tabs.Panel>

            <Tabs.Panel value="analytics" pt="md">
              <PresenceAnalyticsPage />
            </Tabs.Panel>

            <Tabs.Panel value="password" pt="md">
              <PasswordChangeTab userId={authUser.id} user={userData} />
            </Tabs.Panel>

            <Tabs.Panel value="devices" pt="md">
              <DevicesTab
                userId={authUser.id}
                devices={devices}
                onDeviceDelete={handleDeviceDelete}
                onDeviceToggle={handleDeviceToggle}
                onRefresh={fetchDevices}
                loading={devicesLoading}
              />
            </Tabs.Panel>

            <Tabs.Panel value="activity" pt="md">
              <LoginActivityTab
                userId={authUser.id}
                loginAttempts={loginAttempts}
                onRefresh={fetchLoginActivity}
                loading={loginActivityLoading}
              />
            </Tabs.Panel>
          </Tabs>
        </SlideIn>
      </Stack>
    </Container>
  );
}
